import { html, LitElement, unsafeCSS, type CSSResultGroup, type PropertyValues, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { debounce, emit, localiseLabel, localiseMarketingUrl, addUtmQueryParams } from '@/navbar/utils'
import { isEscape, isEnter, isSpace } from '@/navbar/utils/eventKeys'
import { $t, localized } from '@/navbar/i18n'
import { NAMESPACE_LABELS } from '@/navbar/constants/namespace-labels'
import type { UserProduct, NavbarAppSwitcherMarketingData, NavbarAppSwitcherTaxonomyCapability, NavbarAppSwitcherFilteredGroup } from '@/navbar/navbar.types'

// Styles:
import styles from './eco-suite-navbar-app-switcher-search.scss?inline'

// Child components:
import '../tooltip/eco-suite-navbar-tooltip.component'
import '../menu-divider/eco-suite-navbar-menu-divider.component'

/**
 * @internal
 */
@localized()
@customElement('eco-suite-navbar-app-switcher-search')
export class EcoSuiteNavbarAppSwitcherSearch extends LitElement {
  static readonly styles: CSSResultGroup = [unsafeCSS(styles)]

  @query('input')
  private readonly searchInput!: HTMLInputElement

  @property({ type: Array })
  userProducts!: UserProduct[]

  @property({ attribute: false })
  marketingData?: NavbarAppSwitcherMarketingData

  @property({ attribute: false })
  currentProduct = ''

  @property({ type: Boolean, reflect: true })
  open = false

  @state()
  private searchQuery = ''

  @state()
  private filteredSearchQuery = ''

  @state()
  private searchResults: NavbarAppSwitcherFilteredGroup[] = []

  private readonly updateFilteredQuery = debounce((query: string) => {
    this.filteredSearchQuery = query
  }, 200)

  protected willUpdate(changedProps: PropertyValues) {
    const u = (prop: string) => changedProps.has(prop)

    if (u('open')) {
      this.searchQuery = ''
      this.filteredSearchQuery = ''
      if (this.open) this.searchInput?.focus()
    }

    if (u('searchQuery')) {
      if (this.searchQuery) {
        this.updateFilteredQuery(this.searchQuery)
      } else {
        this.filteredSearchQuery = ''
      }
    }

    if (u('filteredSearchQuery') || u('userProducts') || u('marketingData')) {
      this.searchResults = this.computeSearchResults()
    }
  }

  private getUserProduct(productIdentifier: string): UserProduct | undefined {
    return this.userProducts.find(up => up.identifier === productIdentifier)
  }

  private getUserProductForNamespace(products: string[]): UserProduct | undefined {
    return products.map(p => this.getUserProduct(p)).find(Boolean)
  }

  /**
   * Returns a UserProduct if the user owns any product belonging to the given namespace.
   */
  private getUserNamespaceProduct(namespaceIdentifier: string): UserProduct | undefined {
    if (!this.marketingData) return undefined
    const ns = this.marketingData.namespaces.find(n => n.identifier === namespaceIdentifier)
    if (!ns) return undefined
    return this.getUserProductForNamespace(ns.products)
  }

  private handleSearchInput() {
    emit(this, 'eco-search-opened')
  }

  private handleSearchClose() {
    emit(this, 'eco-search-closed')
  }

  private computeSearchResults(): NavbarAppSwitcherFilteredGroup[] {
    if (!this.marketingData) return []

    const query = this.filteredSearchQuery.toLowerCase().trim()
    const matches = (text: string) => text.toLowerCase().includes(query)

    // Group capabilities by namespace_identifier
    const grouped = this.marketingData.capabilities.reduce((groups: Record<string, NavbarAppSwitcherTaxonomyCapability[]>, capability) => {
      const key = capability.namespace_identifier
      if (!groups[key]) groups[key] = []
      groups[key].push(capability)
      return groups
    }, {})

    return Object.entries(grouped)
      .map(([namespaceIdentifier, capabilities]) => {
        const namespace = this.marketingData?.namespaces.find(n => n.identifier === namespaceIdentifier)
        const namespaceName = NAMESPACE_LABELS[namespaceIdentifier] ?? namespaceIdentifier

        if (!query) {
          return { namespaceIdentifier, namespace, namespaceName, capabilities }
        }

        const namespaceMatches = matches(namespaceName)
        const matchedCapabilities = capabilities.filter(c => {
          const label = localiseLabel(c.labels) ?? ''
          return matches(label)
        })

        if (namespaceMatches) {
          return { namespaceIdentifier, namespace, namespaceName, capabilities: matchedCapabilities }
        }

        if (matchedCapabilities.length) {
          return { namespaceIdentifier, namespace, namespaceName, capabilities: matchedCapabilities }
        }

        return null
      })
      .filter((group): group is NonNullable<typeof group> => group !== null)
  }

  private renderSearchResults() {
    if (!this.searchResults.length && this.filteredSearchQuery) {
      return html`<p class="AppSwitcherSearch__no-results">${$t('appSwitcher.noResults')}</p>`
    }

    const utmComponent = 'navbar-app-switcher-search'

    return this.searchResults.map(({ namespaceIdentifier, namespace, namespaceName, capabilities }) => {
      // For the namespace row, use the user's namespace product (if owned) to get the go link
      const userNamespaceProduct = this.getUserNamespaceProduct(namespaceIdentifier)
      const internalProductUrl = userNamespaceProduct?.links.goToApplication
      const localisedUrl = localiseMarketingUrl(namespace?.marketing_urls)
      const marketingUrl = addUtmQueryParams(localisedUrl, this.currentProduct, utmComponent)

      const namespaceUrl = internalProductUrl
        ?? marketingUrl
        ?? ''

      return html`
        <div class="AppSwitcherSearch__group">
          <a
            class="AppSwitcherSearch__item AppSwitcherSearch__item--product ${internalProductUrl ? 'AppSwitcherSearch__item--user-active' : ''}"
            href=${namespaceUrl}
            target=${internalProductUrl ? '_self' : '_blank'}
            rel=${internalProductUrl ? nothing : 'noopener noreferrer'}
            aria-label=${internalProductUrl ? namespaceName : `${namespaceName}, ${$t('navbar.opensInNewTab')}`}
          >
            <span>${namespaceName}</span>
            <pwc-icon-shadow
              icon=${internalProductUrl ? 'arrow-right' : 'external-link'}
              size="xs"
              aria-hidden="true"
            ></pwc-icon-shadow>
          </a>
          ${capabilities.map(c => {
            // For capability deep-links, look up by the capability's specific product_identifier
            // Currently we can't append the internal path to the in-product URLs as they route through Platform
            const userProduct = this.getUserProduct(c.product_identifier)
            const productUrl = userProduct?.links.goToApplication
            const localisedUrl = localiseMarketingUrl(c.marketing_urls)
            const marketingUrl = addUtmQueryParams(localisedUrl, this.currentProduct, utmComponent)

            const capabilityUrl = productUrl
              ?? internalProductUrl
              ?? marketingUrl
              ?? ''

            // Check if we are using an actual product URL instead of a marketing URL
            const internalCapabilityUrl = !!(productUrl || internalProductUrl)

            return html`
              <a
                class="AppSwitcherSearch__item ${internalCapabilityUrl ? 'AppSwitcherSearch__item--user-active' : ''}"
                href=${capabilityUrl}
                target=${internalCapabilityUrl ? '_self' : '_blank'}
                rel=${internalCapabilityUrl ? nothing : 'noopener noreferrer'}
                aria-label=${internalCapabilityUrl
                  ? (localiseLabel(c.labels) ?? '')
                  : `${localiseLabel(c.labels) ?? ''}, ${$t('navbar.opensInNewTab')}`}
              >
                <span>${localiseLabel(c.labels)}</span>
                <pwc-icon-shadow
                  icon=${internalCapabilityUrl ? 'arrow-right' : 'external-link'}
                  size="xs"
                  aria-hidden="true"
                ></pwc-icon-shadow>
              </a>
            `
          })}
        </div>
        <eco-suite-navbar-menu-divider></eco-suite-navbar-menu-divider>
      `
    })
  }

  render() {
    return html`
      <div
        class="AppSwitcherSearch ${this.open ? 'AppSwitcherSearch--open' : ''}"
        @keydown=${(e: KeyboardEvent) => {
          if (this.open && isEscape(e)) {
            e.preventDefault()
            e.stopPropagation()
            this.handleSearchClose()
          }
        }}
      >
        <div class="AppSwitcherSearch__bar">
          <pwc-icon-shadow icon="search" size="sm" aria-hidden="true"></pwc-icon-shadow>
          <input
            placeholder="${$t('appSwitcher.findApps')}..."
            aria-label=${$t('appSwitcher.findApps')}
            .value=${this.searchQuery}
            @input=${(e: Event) => {
              if (!this.open) this.handleSearchInput()
              this.searchQuery = (e.target as HTMLInputElement).value
            }}
            @mousedown=${() => {
              if (!this.open) this.handleSearchInput()
            }}
          >
          <eco-suite-navbar-tooltip content=${$t('appSwitcher.closeSearch')} class="AppSwitcherSearch__close-btn">
            <pwc-icon-button
              icon="cross"
              size="sm"
              aria-label=${$t('appSwitcher.closeSearch')}
              @click=${this.handleSearchClose}
              @keydown=${(e: KeyboardEvent) => {
                if (isEnter(e) || isSpace(e)) {
                  this.handleSearchClose()
                }
              }}
            ></pwc-icon-button>
          </eco-suite-navbar-tooltip>
        </div>

        <div class="AppSwitcherSearch__results" aria-live="polite" aria-atomic="false">
          ${this.renderSearchResults()}
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'eco-suite-navbar-app-switcher-search': EcoSuiteNavbarAppSwitcherSearch
  }
}
